import React, { useState, useEffect } from "react";
import axios from "axios";
import { BsInfoCircle } from "react-icons/bs";
import { NavLink, useNavigate } from "react-router-dom";
import { IoCheckmark } from "react-icons/io5";
import { CiMonitor } from "react-icons/ci";
import { GoKey, GoPencil, GoSignOut } from "react-icons/go";
import {
  AiOutlineMail,
  AiOutlineEye,
  AiOutlineEyeInvisible,
} from "react-icons/ai";
import { MdOutlineDeleteOutline } from "react-icons/md";
import UpdateAccount from "./ui/UpdateAccount";

const EditProfile = ({ handleLogout }) => {
  // State Management
  const [userData, setUserData] = useState([]);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [region, setRegion] = useState("");
  const [userType, setUserType] = useState("");
  const [designation, setDesignation] = useState("");

  const [isDesgEdit, setDesgEdit] = useState(false);
  const [isEmailEdit, setEmailEdit] = useState(false);
  const [isPasswordEdit, setPasswordEdit] = useState(false);
  const [isEditFormVisible, setIsEditFormVisible] = useState(false);

  // Subscription State
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubLoading, setIsSubLoading] = useState(true);

  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const url = "https://api.confidanto.com/header-data";

  // Data Fetching
  const fetchInfo = async () => {
    try {
      const res = await axios.post(url, {
        email: localStorage.getItem("email"),
      });
      const data = res.data.userData;
      setUserData([data]);
      setEmail(data.email);
      setUsername(data.username);
      setOrganisation(data.organisation);
      setRegion(data.region);
      setDesignation(data.designation);
      setUserType(data.userType);
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  const fetchSubscription = async () => {
    const userEmail = localStorage.getItem("email");
    if (!userEmail) {
      setIsSubLoading(false);
      return;
    }
    try {
      const res = await axios.post(
        "https://api.confidanto.com/subscriptions/get",
        {
          email: userEmail,
        },
      );
      // Check response structure for subscription status (adjust if your API returns a different key)
      const status =
        res.data?.isSubscribed ||
        res.data?.subscribed ||
        res.data?.status === "active";
      setIsSubscribed(!!status);
    } catch (error) {
      console.error("Error fetching subscription status:", error);
      setIsSubscribed(false);
    } finally {
      setIsSubLoading(false);
    }
  };

  const toggleSubscription = async () => {
    const userEmail = localStorage.getItem("email");
    if (!userEmail) return;

    setIsSubLoading(true);
    try {
      if (isSubscribed) {
        await axios.post(
          "https://api.confidanto.com/subscriptions/unsubscribe",
          {
            email: userEmail,
          },
        );
        setIsSubscribed(false);
      } else {
        await axios.post("https://api.confidanto.com/subscriptions/subscribe", {
          email: userEmail,
        });
        setIsSubscribed(true);
      }
    } catch (error) {
      console.error("Error toggling subscription:", error);
      alert(
        "An error occurred while updating your subscription. Please try again.",
      );
    } finally {
      setIsSubLoading(false);
    }
  };

  const handleSavePasswordChanges = () => {
    if (newPassword !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    if (
      /[\#\$\&\@]/.test(newPassword) &&
      newPassword.length >= 6 &&
      /(?=.*[a-z])(?=.*[A-Z])/.test(newPassword)
    ) {
      axios
        .post("https://api.confidanto.com/update-profile/change-password", {
          email: localStorage.getItem("email"),
          currentPassword: userData[0].password,
          newPassword: confirmPassword,
        })
        .then(() => {
          alert("Password successfully updated.");
          handlePasswordEdit();
        })
        .catch((err) => {
          console.error("Error Saving Password:", err);
        });
    } else {
      alert("Your password does not meet the required format.");
    }
  };

  const handleDeleteAccount = async () => {
    const userEmail = localStorage.getItem("email");
    if (!userEmail) {
      alert("Email not found. Please log in again.");
      return;
    }

    if (
      window.confirm(
        "Are you sure you want to delete your account? This action cannot be undone.",
      )
    ) {
      try {
        const response = await axios.post(
          "https://api.confidanto.com/delete-account",
          { email: userEmail },
        );

        if (response.status === 200) {
          alert("Account deleted successfully.");
          localStorage.clear();
          logout();
        } else {
          alert("Failed to delete account. Please try again.");
        }
      } catch (error) {
        console.error("Error deleting account:", error);
        alert("An error occurred. Please try again.");
      }
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    handleLogout();
    navigate("/");
  };

  // Handlers
  const handleDesignationChange = (e) => setDesignation(e.target.value);
  const handleDesignationEdit = () => setDesgEdit(!isDesgEdit);
  const handleEmailEdit = () => setEmailEdit(!isEmailEdit);
  const handlePasswordEdit = () => {
    setPasswordEdit(!isPasswordEdit);
    setNewPassword("");
    setConfirmPassword("");
  };

  useEffect(() => {
    fetchInfo();
    fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50/50 text-gray-800 pb-16 font-sans">
      {/* Page Header (Full Width with generous padding) */}
      <div className="w-full mx-auto px-4 md:px-8 lg:px-12 2xl:px-24 pt-8 sm:pt-12 pb-8">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
          Account Settings
        </h1>
        <p className="mt-2 text-sm sm:text-base text-gray-500">
          Manage your account details, security preferences, and organization
          info.
        </p>
      </div>

      {/* Main Grid Layout - Spanning wide nicely */}
      <div className="w-full mx-auto px-4 md:px-8 lg:px-12 2xl:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
          {/* Left Column (Forms & Info) */}
          <div className="lg:col-span-2 space-y-6 lg:space-y-8">
            {/* Account Info Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              {isEditFormVisible ? (
                <div className="p-4 sm:p-8">
                  <UpdateAccount
                    setIsEditFormVisible={setIsEditFormVisible}
                    fetchInfo={fetchInfo}
                    userData={userData[0]}
                  />
                </div>
              ) : (
                <>
                  <div className="px-5 sm:px-8 py-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/30">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2.5">
                      <BsInfoCircle
                        className="text-blue-500 flex-shrink-0"
                        size={18}
                      />{" "}
                      General Information
                    </h2>
                    <button
                      className="text-gray-500 hover:text-blue-600 transition-colors p-2 rounded-md hover:bg-blue-50"
                      onClick={() => setIsEditFormVisible(true)}
                      title="Edit Profile"
                    >
                      <GoPencil size={18} />
                    </button>
                  </div>

                  <div className="p-5 sm:p-8 space-y-6 sm:space-y-7">
                    {userData.map((dataObj, idx) => (
                      <React.Fragment key={idx}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              First Name
                            </label>
                            <input
                              type="text"
                              value={dataObj.username.split(" ")[0] || ""}
                              readOnly
                              className="w-full bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-xl p-3 focus:ring-0 cursor-not-allowed"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Last Name
                            </label>
                            <input
                              type="text"
                              value={
                                dataObj.username
                                  .split(" ")
                                  .slice(1)
                                  .join(" ") || ""
                              }
                              readOnly
                              className="w-full bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-xl p-3 focus:ring-0 cursor-not-allowed"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Company Name
                            </label>
                            <input
                              type="text"
                              value={dataObj.organisation}
                              readOnly
                              className="w-full bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-xl p-3 focus:ring-0 cursor-not-allowed"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Location
                            </label>
                            <input
                              type="text"
                              value={dataObj.region}
                              readOnly
                              className="w-full bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-xl p-3 focus:ring-0 cursor-not-allowed"
                            />
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Login & Security Info Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 sm:px-8 py-5 border-b border-gray-100 bg-gray-50/30">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2.5">
                  <GoKey className="text-blue-500 flex-shrink-0" size={18} />{" "}
                  Authentication & Role
                </h2>
              </div>

              <div className="p-5 sm:p-8 space-y-6 sm:space-y-7">
                {userData.map((dataObj, idx) => (
                  <React.Fragment key={idx}>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <AiOutlineMail className="text-gray-400" size={16} />{" "}
                        Account Email
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          value={newEmail ? newEmail : dataObj.email}
                          disabled={!isEmailEdit}
                          onChange={(e) => setNewEmail(e.target.value)}
                          className={`w-full border text-sm rounded-xl p-3 pr-12 transition-all ${
                            isEmailEdit
                              ? "bg-white border-blue-300 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500"
                              : "bg-gray-50/50 border-gray-200 text-gray-500 cursor-not-allowed"
                          }`}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-blue-600 transition-colors"
                          onClick={handleEmailEdit}
                        >
                          <GoPencil size={18} />
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <GoKey className="text-gray-400" size={16} /> Password
                      </label>
                      <div className="relative">
                        <input
                          type="password"
                          value="••••••••••••"
                          disabled
                          className="w-full bg-gray-50/50 border border-gray-200 text-gray-500 text-sm rounded-xl p-3 pr-12 cursor-not-allowed"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-blue-600 transition-colors"
                          onClick={handlePasswordEdit}
                        >
                          <GoPencil size={18} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8 pt-2">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          User Role
                        </label>
                        <input
                          type="text"
                          value={dataObj.userType}
                          readOnly
                          className="w-full bg-gray-50/50 border border-gray-200 text-gray-500 capitalize text-sm rounded-xl p-3 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Designation
                        </label>
                        <div className="relative">
                          {isDesgEdit ? (
                            <select
                              value={designation}
                              onChange={handleDesignationChange}
                              className="w-full bg-white border border-blue-300 text-gray-900 text-sm rounded-xl p-3 pr-10 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all appearance-none"
                            >
                              <option value="">Select Designation</option>
                              {[
                                "Founder",
                                "Co-founder",
                                "Senior Manager",
                                "Manager",
                                "Employee",
                                "Creative Director",
                                "Art Director",
                                "SEO Specialist",
                                "Content Strategist",
                                "Public Relations Specialist",
                                "Event Coordinator",
                                "Copywriter",
                                "Graphic Designer",
                                "Account Executive",
                                "Media Planner",
                                "Digital Marketing Specialist",
                                "Social Media Manager",
                              ].map((option) => (
                                <option
                                  key={option}
                                  value={option.replace(" ", "_")}
                                >
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={
                                designation ? designation : dataObj.designation
                              }
                              readOnly
                              className="w-full bg-gray-50/50 border border-gray-200 text-gray-500 text-sm rounded-xl p-3 pr-12 cursor-not-allowed"
                            />
                          )}
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-blue-600 pointer-events-auto transition-colors"
                            onClick={handleDesignationEdit}
                          >
                            <GoPencil size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column (Actions & Status) */}
          <div className="space-y-6 lg:space-y-8">
            {/* Status Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-6">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-5">
                Account Status
              </h3>

              <div className="flex items-start gap-4 mb-6">
                <div className="bg-green-100 text-green-600 p-2.5 rounded-full mt-0.5 flex-shrink-0">
                  <IoCheckmark size={18} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">
                    Email Verified
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Your email address has been successfully confirmed.
                  </p>
                </div>
              </div>

              <hr className="border-gray-100 my-5" />

              <div className="flex items-start gap-4">
                <div className="bg-blue-100 text-blue-600 p-2.5 rounded-full mt-0.5 flex-shrink-0">
                  <CiMonitor size={18} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">
                    Recent Activity
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Active on Mac, Chrome
                  </p>
                  <p className="text-xs text-gray-400 mt-1.5 font-medium">
                    Pune, India • Just now
                  </p>
                </div>
              </div>
            </div>

            {/* Combined Settings & Action Cards */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
              {/* Subscription Toggle Row */}
              <div className="p-5 sm:p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:bg-gray-50/50 transition-colors border-b border-gray-100">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    Email Subscriptions
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Receive product updates, alerts.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggleSubscription}
                  disabled={isSubLoading}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isSubscribed ? "bg-blue-600" : "bg-gray-200"
                  } ${isSubLoading ? "opacity-50 cursor-wait" : ""}`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      isSubscribed ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {/* Sign Out Row */}
              <div className="p-5 sm:p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:bg-gray-50/50 transition-colors border-b border-gray-100">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    Sign Out
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    End your current session securely.
                  </p>
                </div>
                <button
                  onClick={logout}
                  className="w-full sm:w-auto px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-100 transition-all flex items-center justify-center gap-2"
                >
                  <GoSignOut size={16} /> Logout
                </button>
              </div>

              {/* Danger Zone Row */}
              <div className="p-5 sm:p-6  hover:bg-red-50/60 transition-colors">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
                  <div>
                    <p className="text-sm text-gray-500 sm:max-w-xs mt-1">
                      Permanently remove your account and all associated data.
                      This cannot be undone.
                    </p>
                  </div>
                  <button
                    onClick={handleDeleteAccount}
                    className="w-full sm:w-auto px-5 py-2.5 text-sm font-semibold text-red-600 bg-white border border-red-200 rounded-xl hover:bg-red-50 hover:border-red-300 focus:outline-none focus:ring-4 focus:ring-red-100 transition-all flex items-center justify-center gap-2"
                  >
                    <MdOutlineDeleteOutline size={18} /> Delete Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Edit Modal Overlay */}
      {isPasswordEdit && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm z-50 p-4 transition-opacity">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
            <div className="sticky top-0 bg-white z-10 px-6 py-5 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">
                Update Password
              </h2>
              <button
                className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full p-1.5 transition-all"
                onClick={handlePasswordEdit}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full bg-white border border-gray-300 text-gray-900 text-sm rounded-xl p-3 pr-10 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                    placeholder="Enter new password"
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <AiOutlineEyeInvisible size={18} />
                    ) : (
                      <AiOutlineEye size={18} />
                    )}
                  </button>
                </div>

                {newPassword.length > 0 && (
                  <div className="mt-3 space-y-1.5 text-xs font-medium">
                    <p
                      className={
                        newPassword.length >= 6
                          ? "text-green-600 flex items-center gap-1.5"
                          : "text-gray-400 flex items-center gap-1.5"
                      }
                    >
                      {newPassword.length >= 6 ? "✓" : "○"} At least 6
                      characters
                    </p>
                    <p
                      className={
                        /(?=.*[a-z])(?=.*[A-Z])/.test(newPassword)
                          ? "text-green-600 flex items-center gap-1.5"
                          : "text-gray-400 flex items-center gap-1.5"
                      }
                    >
                      {/(?=.*[a-z])(?=.*[A-Z])/.test(newPassword) ? "✓" : "○"}{" "}
                      Upper & lowercase letters
                    </p>
                    <p
                      className={
                        /[\#\$\&\@]/.test(newPassword)
                          ? "text-green-600 flex items-center gap-1.5"
                          : "text-gray-400 flex items-center gap-1.5"
                      }
                    >
                      {/[\#\$\&\@]/.test(newPassword) ? "✓" : "○"} At least one
                      symbol (@, #, $, &)
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className={`w-full bg-white border text-gray-900 text-sm rounded-xl p-3 pr-10 outline-none transition-all ${
                      confirmPassword && newPassword !== confirmPassword
                        ? "border-red-300 focus:ring-4 focus:ring-red-500/10 focus:border-red-500"
                        : "border-gray-300 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500"
                    }`}
                    placeholder="Confirm new password"
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <AiOutlineEyeInvisible size={18} />
                    ) : (
                      <AiOutlineEye size={18} />
                    )}
                  </button>
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-red-500 text-xs font-medium mt-2">
                    Passwords do not match
                  </p>
                )}
              </div>

              {/* Action buttons stack on very small screens, row on sm+ */}
              <div className="pt-5 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                <button
                  type="button"
                  className="w-full sm:w-auto px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:ring-4 focus:ring-gray-100 transition-all"
                  onClick={handlePasswordEdit}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`w-full sm:w-auto px-5 py-2.5 text-sm font-semibold text-white rounded-xl transition-all ${
                    !newPassword || newPassword !== confirmPassword
                      ? "bg-blue-300 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700 shadow-md focus:ring-4 focus:ring-blue-500/30"
                  }`}
                  onClick={handleSavePasswordChanges}
                  disabled={!newPassword || newPassword !== confirmPassword}
                >
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProfile;
